import {
  Button,
  CircularProgress,
  Grid,
  Theme,
  Typography,
  withStyles,
  useMediaQuery
} from '@material-ui/core'
import DateRangeIcon from '@material-ui/icons/DateRange'
import TimeRangeIcon from '@material-ui/icons/WatchLaterOutlined'
import { TFunction } from 'i18next'
import { isNull } from 'lodash'
import moment from 'moment'
import { FC, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import {
  addShowingToFieldArray,
  createShowings
} from '../../../actions/showings'
import {
  ReduxDatePicker,
  ReduxTimePickerWithoutKeyboard
} from '../../../components/FormInput/ReduxWrappers'
import {
  selectShowingsError,
  selectShowingsLoading
} from '../../../selectors/showings'
import { MuiClasses } from '../../../types'
import { Application } from '../../../types/applications'
import { ReduxForm } from '../../../types/forms'
import { AddShowingsFormData, FormShowing } from '../../../types/showings'
import { CreateShowingsFormState, StateInterface } from '../../../types/states'
import { bindSubmitActionToPromise } from '../../../utils/forms'
import Suggest from '../../Apartment/Stepper/Steps/Suggest'
const styles = ({ breakpoints, spacing }: Theme) => ({
  modalContainer: {
    padding: '24px',
    [breakpoints.down('sm')]: {
      padding: '0'
    }
  },
  modalItem: {
    fontSize: '1.3rem',
    marginBottom: '15px',
    [breakpoints.down('sm')]: {
      width: '100%'
    },
    [breakpoints.only('xs')]: {
      marginBottom: '0'
    }
  },
  primaryBtn: {
    backgroundColor: '#4f9b44',
    width: '100%',
    padding: '15px 0',
    borderRadius: '10px',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#3f7c36'
    }
  },
  phoneLink: {
    color: '#4F9B44',
    textDecoration: 'none',
    marginBottom: '15px',
    [breakpoints.only('xs')]: {
      display: 'block'
    }
  },
  datePicker: {
    position: 'relative' as const,
    width: '100%',
    textAlign: 'left' as const,
    '& > label': {
      fontFamily: 'CircularRegular',
      display: 'inline-block',
      paddingBottom: '0.5rem'
    },
    '& .MuiInput-underline': {
      margin: '0',
      '&::after': {
        content: 'none !important'
      },
      '&::before': {
        content: 'none !important'
      }
    },
    '& > svg': {
      display: 'inline-block',
      height: '1rem',
      width: '1rem',
      content: '""',
      background: 'transparent',
      color: 'gray',
      position: 'absolute' as const,
      top: '35%',
      left: '10px',
      zIndex: '15',
      cursor: 'pointer',
      pointerEvents: 'none'
    },
    '& .MuiTextField-root': {
      '& label': {
        zIndex: '15',
        display: 'none',
        position: 'absolute',
        margin: '0'
      },
      '& input': {
        background: '#E7ECEF',
        fontSize: '1.1rem',
        cursor: 'pointer',
        zIndex: '10',
        padding: '12px 21px 10px 35px',
        borderRadius: '6px',
        border: 'none',
        '&::after': {
          content: 'none !important'
        }
      }
    },
    '& .Mui-error': {
      fontSize: '11px',
      margin: '0',
      marginTop: '3px',
      textAlign: 'left',
      fontFamily: 'CircularRegular',
      'font-weight': '400',
      lineHeight: '1.66',
      letterSpacing: '0.03333em'
    }
  }
})
interface AddShowingsFormProps extends ReduxForm {
  application: Application
  classes: MuiClasses
  t: TFunction
  formDate: string
  formTime: string
  showings: FormShowing[]
  showingsLoading: boolean
  addShowingToFieldArray: (formData: AddShowingsFormData) => string
  createShowingsAction: (formData: CreateShowingsFormState) => void
  customError: string | null
}

const DirectCreationModal: FC<AddShowingsFormProps> = ({
  classes,
  application,
  t,
  handleSubmit,
  addShowingToFieldArray,
  createShowingsAction,
  error,
  formDate,
  formTime,
  showings,
  showingsLoading,
  customError
}) => {
  const [required, setRequired] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [newShowingCreated, setNewShowingCreated] = useState(false)

  const isSmallScreen = useMediaQuery('(max-width:600px)');

  const handleAddShowingsToArray = async () => {
    if (formDate && formTime) {
      setRequired('')
      setIsLoading(true)
      const phoneShowingPickingError = localStorage.getItem("phoneShowingPickingError")
      if (phoneShowingPickingError == "true") {
        setIsLoading(false)
      }
      await addShowingToFieldArray({
        date: moment(formDate),
        startDate: moment(formTime),
        directCreation: true,
      })

      setNewShowingCreated(true)
    } else {
      setRequired('apartment.actions.stepFail')
    }
  }

  useEffect(() => {
    if (customError) {
      setIsLoading(false);
    }
    if (newShowingCreated && isNull(customError) && showings && showings.length > 0) {
      const lastShowing = showings[showings.length - 1]

      setIsLoading(true)

      createShowingsAction({
        showings: [lastShowing],
        confirmImmediately: true,
        selectedApplication: [application.applicationId],
      })

      setNewShowingCreated(false)
    }
  }, [newShowingCreated, showings, customError])


  return (
    <div className={classes.modalContainer}>
      <form>
        <Typography variant='h6' className={classes.modalItem}>
          {t('showings.createShowings.phone')}:{' '}
          <a
            className={classes.phoneLink}
            href={`tel:+${application.data.phone}`}
          >
            +{application.data.phone}
          </a>
        </Typography>
        <Grid container spacing={2} style={{ paddingTop: '10px' }}>
          <Grid item sm={6} className={classes.modalItem}>
            <Typography variant='body1' gutterBottom>
              {t('showings.createShowings.form.selectDate')}
            </Typography>
            <Grid item xs={12} className={classes.datePicker}>
              <DateRangeIcon />
              <Field
                fullWidth
                name='date'
                component={ReduxDatePicker}
                disablePast={true}
              />
            </Grid>
          </Grid>
          <Grid item sm={6} className={classes.modalItem}>
            <Typography variant='body1' gutterBottom>
              {t('showings.createShowings.form.selectTime')}
            </Typography>
            <Grid item xs={12} className={classes.datePicker}>
              <TimeRangeIcon />
              <Field
                fullWidth
                name='startDate'
                component={ReduxTimePickerWithoutKeyboard}
                minutesStep={5}
                variant='contained'
              />
            </Grid>
          </Grid>
          {(required || customError || error) && (
            <Grid item xs={12} style={{ color: 'red', textAlign: 'center' }}>
              {required && <Typography>{t(required)}</Typography>}
              {customError && (
                <Typography>{t(`error.showings.${customError}`)}</Typography>
              )}
              <Typography>{error}</Typography>
            </Grid>
          )}

          <Grid item xs={12} style={{
            paddingTop: isSmallScreen ? '20px' : '5px'
          }}>
            <Button
              onClick={handleAddShowingsToArray}
              className={classes.primaryBtn}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress
                  size={25}
                  style={{ color: '#fff', marginLeft: '5px' }}
                />
              ) : (
                <>{t('showings.createShowings.form.confirmShowingByPhone')}</>
              )}
            </Button>
          </Grid>
          <Grid item xs={12} style={{ marginTop: '2px' }}>
            <Grid item xs={12}>
              <Suggest
                containerMaxWidth='900px'
                contentMaxWidth='900px'
                content={t('showings.hints.directCreation')}
                isModal
                padding="5px 0 15px 0"
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
    </div>
  )
}
const mapStateToProps = (state: StateInterface) => ({
  showings: formValueSelector('CreateShowingsForm')(state, 'showings'),
  formDate: formValueSelector('AddShowingsForm')(state, 'date'),
  formTime: formValueSelector('AddShowingsForm')(state, 'startDate'),
  customError: selectShowingsError(state),
  showingsLoading: selectShowingsLoading(state)
})
const mapDispatchToProps = (dispatch: Dispatch) => ({
  addShowingToFieldArray: bindSubmitActionToPromise(
    dispatch,
    addShowingToFieldArray
  ),
  createShowingsAction: bindSubmitActionToPromise(dispatch, createShowings)
})
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: 'AddShowingsForm'
  })(withStyles(styles)(DirectCreationModal) as any) as any
) as any
